import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "edit-form-section d-flex flex-column p-2 p-lg-0" }
const _hoisted_2 = { class: "title-2 text-primary-black" }
const _hoisted_3 = { class: "button-container d-flex justify-content-md-end mt-4" }

import { ComputedRef, computed, ref, Ref } from 'vue'

  // Types
  import { UserInfo } from '@/services/AuthService.interface'
  import { UpdateUserInfoFn, ValidationRules,EditFormFields } from './EditInfoForm.types'

  // Composables
  import { useValidation } from '@/composables/useValidation'
  import { useIsPhoneNumberValid } from '@/composables/useIsPhoneNumberValid'

  // Utils
  import { validateName } from '@/utils'

  // Components
  import InfoFields from './InfoFields.vue'
  import ContactFields from './ContactFields.vue'
  import Button from '@/components/UI/Button.vue'
  import InputFieldError from '@/components/UI/InputFieldError.vue'

  // Config
  import { ModalId } from '@/config/modalIds'

  // Translations
  import { useI18n } from 'vue-i18n'
  import { useStore } from '@/store'
  
export default /*@__PURE__*/_defineComponent({
  __name: 'EditInfoForm',
  props: {
    editable: {
      type: Boolean,
      required: true,
    },
  },
  setup(__props) {

  // Vue
  const { t } = useI18n()

  // Store
  const store = useStore()

  // Props
  

  // Computed
  const {isPhoneNumberValid} = useIsPhoneNumberValid()


  // Store data
  const userInfo: ComputedRef<UserInfo> = computed(() => {
    const user = store.state.auth?.user
    if (!user) throw new Error('user must be defined in EditInfoForm')
    return user
  })

  const isLoading: ComputedRef<boolean> = computed(() => {
    const isLoadingGetter = store.getters['auth/isLoading']
    return isLoadingGetter('updateUserInfo')
  })

  const updateError: ComputedRef<string | null> = computed(() => {
    const errorGetter = store.getters['auth/getError']
    return errorGetter('updateUserInfo')
  })

  // Refs
  // Edit form values
  const formValues: Ref<Partial<UserInfo>> = ref({
    first_name: userInfo.value.first_name,
    last_name: userInfo.value.last_name,
    address: userInfo.value.address,
    city: userInfo.value.city,
    country: userInfo.value.country,
    phone_code: userInfo.value.phone_code,
    postal_code: userInfo.value.postal_code,
    phone_number: userInfo.value.phone_number.replace(
      userInfo.value.phone_code,
      ''
    ), // remove the phone code from the value
  })

  // Map of invalid fields
  const validationRules: ValidationRules = {
    first_name: (value: string) => validateName(value),
    last_name: (value: string) => validateName(value),
    address: (value: string) => validateName(value),
    city: (value: string) => validateName(value),
    country: (value: string) => validateName(value),
    phone_code: (value: string) => validateName(value),
    postal_code: (value: string) => validateName(value),
    phone_number: (value: string, payload?: EditFormFields) => {
        if (!payload || !payload.phone_code) return false;
        return isPhoneNumberValid(`${payload.phone_code}${value}`);
    },    
  }

  const { invalidFields } = useValidation<Partial<UserInfo>>(
    formValues,
    validationRules
  )

  // Methods

  // Update function to pass to subforms
  const updateFormValues: UpdateUserInfoFn = (key, value) => {
    formValues.value[key] = value
  }

  const handleSave = async (): Promise<void> => {
    try {
      // Merge the updated fields
      const updates: Partial<UserInfo> = {
        ...formValues.value,
      }
      await store.dispatch('auth/updateUserInfo', updates)

      store.commit('modal/close', ModalId.EDIT_INFO_PANEL)
    } catch (err) {
      console.error(err)
    }
  }

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("h2", _hoisted_2, _toDisplayString(_unref(t)('profile.myInfo')), 1),
    _createElementVNode("div", null, [
      _createVNode(InfoFields, {
        "user-info": userInfo.value,
        "form-values": formValues.value,
        updateFN: updateFormValues,
        "invalid-fields": _unref(invalidFields),
        loading: isLoading.value,
        editable: __props.editable
      }, null, 8, ["user-info", "form-values", "invalid-fields", "loading", "editable"]),
      _createVNode(ContactFields, {
        "user-info": userInfo.value,
        "form-values": formValues.value,
        updateFN: updateFormValues,
        "invalid-fields": _unref(invalidFields),
        loading: isLoading.value,
        editable: __props.editable
      }, null, 8, ["user-info", "form-values", "invalid-fields", "loading", "editable"])
    ]),
    _createElementVNode("div", _hoisted_3, [
      _createVNode(Button, {
        onButtonClick: handleSave,
        class: "col-12 col-md-6",
        disabled: !__props.editable || isLoading.value || _unref(invalidFields).size !== 0,
        loading: isLoading.value
      }, {
        text: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref(t)('profile.editInfoForm.save')), 1)
        ]),
        _: 1
      }, 8, ["disabled", "loading"])
    ]),
    (updateError.value)
      ? (_openBlock(), _createBlock(InputFieldError, {
          key: 0,
          text: _unref(t)('errorMessages.updateUserInfo')
        }, null, 8, ["text"]))
      : _createCommentVNode("", true)
  ]))
}
}

})